// Typed fetch wrapper shared by the web client now and a future React Native app (§22).
// Deliberately free of DOM/browser APIs - only the global `fetch`.
import { ApiError, copyForCode } from './errors.js';
// A non-JSON failure (proxy/CDN page, empty body) still gets a coded envelope
// with plain copy, so nothing raw ever reaches a toast.
function codeForStatus(status) {
    if (status === 401)
        return 'UNAUTHENTICATED';
    if (status === 403)
        return 'FORBIDDEN';
    if (status === 404)
        return 'NOT_FOUND';
    if (status === 429)
        return 'RATE_LIMITED';
    return 'INTERNAL';
}
function buildQuery(query) {
    if (!query)
        return '';
    const params = new URLSearchParams();
    for (const [k, v] of Object.entries(query)) {
        if (v === undefined || v === null)
            continue;
        params.set(k, String(v));
    }
    const s = params.toString();
    return s ? `?${s}` : '';
}
export class ApiClient {
    opts;
    constructor(opts) {
        this.opts = opts;
    }
    async request(path, options = {}, isRetry = false) {
        const token = await this.opts.getAccessToken();
        const headers = { ...options.headers };
        if (token)
            headers.Authorization = `Bearer ${token}`;
        let body;
        if (options.rawBody) {
            body = options.rawBody;
        }
        else if (options.body !== undefined) {
            headers['Content-Type'] = 'application/json';
            body = JSON.stringify(options.body);
        }
        const controller = new AbortController();
        const timeout = setTimeout(() => controller.abort(), 30_000);
        const signal = options.signal ?? controller.signal;
        let res;
        try {
            res = await fetch(`${this.opts.baseUrl}${path}${buildQuery(options.query)}`, {
                method: options.method ?? 'GET',
                headers,
                body,
                signal,
            });
        }
        finally {
            clearTimeout(timeout);
        }
        if (res.status === 401 && !isRetry && this.opts.onUnauthorized) {
            const newToken = await this.opts.onUnauthorized();
            if (newToken)
                return this.request(path, options, true);
        }
        if (res.status === 304) {
            return undefined;
        }
        if (!res.ok) {
            let envelope;
            try {
                envelope = (await res.json());
            }
            catch {
                const code = codeForStatus(res.status);
                envelope = { error: { code, message: copyForCode(code).body, requestId: 'unknown' } };
            }
            throw new ApiError(res.status, envelope);
        }
        if (res.status === 204)
            return undefined;
        const text = await res.text();
        return (text ? JSON.parse(text) : undefined);
    }
    get(path, options) {
        return this.request(path, { ...options, method: 'GET' });
    }
    post(path, body, options) {
        return this.request(path, { ...options, method: 'POST', body });
    }
    put(path, body, options) {
        return this.request(path, { ...options, method: 'PUT', body });
    }
    patch(path, body, options) {
        return this.request(path, { ...options, method: 'PATCH', body });
    }
    delete(path, body, options) {
        return this.request(path, { ...options, method: 'DELETE', body });
    }
    upload(path, formData, options) {
        return this.request(path, { ...options, method: 'POST', rawBody: formData });
    }
}
export function workspacePath(workspaceId, sub) {
    return `/api/v1/workspaces/${workspaceId}${sub}`;
}
//# sourceMappingURL=api-client.js.map